/**
 * GuildDetail.jsx — widget renderer for the `guild-detail` type.
 *
 * Issue #92 — pairs with the popup→window affordance on GuildDetailModal.
 * Reads `settings.name` (set when promoted from the popup) and re-renders
 * via the shared GuildDetailBody.
 *
 * Sprint-17 #157 — admins additionally see a GuildSearchInput at the top
 * so they can pivot to any non-Redfur guild without leaving the widget.
 */
import { createSignal, createEffect, Show } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import GuildDetailBody from './GuildDetailBody.jsx';
import GuildSearchInput from '../components/GuildSearchInput.jsx';
import wm from '../lib/wmStore.js';

export default function GuildDetail(props) {
  const [name, setName] = createSignal(String(props.settings?.name || props.settings?.guild || '').trim());
  const [pickedId, setPickedId] = createSignal(props.settings?.guildId || null);
  const guildId = () => pickedId() || props.settings?.guildId || null;
  const weekOffset = () => Number(props.settings?.weekOffset || 0);
  const isAdmin = () => !!wm.state.identity?.isAdmin;
  createEffect(() => {
    const sName = String(props.settings?.name || props.settings?.guild || '').trim();
    if (sName && sName !== name()) setName(sName);
  });
  function persistGuild(nextName, nextId) {
    setPickedId(nextId || null);
    setName(nextName || '');
    const wid = props.widget?.id;
    if (wid) {
      wm.setSettings(wid, {
        ...(props.widget?.settings || {}),
        name: nextName || '',
        guildId: nextId || null,
      });
    }
  }
  const { data, loading, error } = useWidgetData('guild-detail', () => ({
    name: name(),
    guildId: guildId(),
    weekOffset: weekOffset(),
  }));
  return (
    <div class="v2-widget">
      <Show when={isAdmin()}>
        <div class="v2-gt-search">
          <GuildSearchInput
            placeholder="Look up any guild…"
            initialValue={name()}
            onSelect={(g) => persistGuild(g.name, g.id)}
            onClear={() => persistGuild('', null)}
          />
        </div>
      </Show>
      <Show when={name() || guildId()} fallback={<div class="v2-empty">No guild set. Pick a guild to populate this window.</div>}>
        <GuildDetailBody
          name={name()}
          data={data()}
          loading={loading()}
          error={error()}
          weekOffset={weekOffset()}
        />
      </Show>
    </div>
  );
}
